/**
 * align.js — the three measurements that decide whether a clone shot reads as
 * one continuous take or as an obvious paste. All of them compare two frames
 * from the two takes and are only meaningful BECAUSE the camera did not move:
 *
 *   1. registration — the tripod WAS bumped a little. Estimate the translation
 *      between the takes so the seam lands on a background that matches.
 *   2. difference map — where the two frames disagree is exactly where a person
 *      is standing. Showing that to the user turns "guess where to put the
 *      line" into "put the line in the dark area", and the same map yields a
 *      suggested seam automatically.
 *   3. tone match — one camera on auto-exposure gives two different exposures.
 *      Measured on the background the frames AGREE about, so a person wearing
 *      white cannot drag the correction with them.
 *
 * Everything runs on downscaled greyscale on the main thread: at 240 px wide a
 * full search is ~1 M byte comparisons, which is a few milliseconds, and the
 * user pressed a button so there is nothing to hide behind a worker.
 *
 * Exposes window.CloneAlign.
 */
(function () {
'use strict';
if (window.CloneAlign) return;

/* ─────────────────────────── sampling helpers ─────────────────────────── */

function grab(src, w, h) {
    var cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    var ctx = cv.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'medium';
    ctx.drawImage(src, 0, 0, w, h);
    return ctx.getImageData(0, 0, w, h);
}

function toGray(img) {
    var d = img.data, n = img.width * img.height;
    var g = new Float32Array(n);
    for (var i = 0, j = 0; i < n; i++, j += 4) {
        g[i] = 0.299 * d[j] + 0.587 * d[j + 1] + 0.114 * d[j + 2];
    }
    return g;
}

/* ─────────────────────────── 1. registration ───────────────────────────
   Mean-removed SAD over an integer shift grid, coarse then fine. Mean removal
   matters: without it a brighter take wins the match at shift (0,0) for the
   wrong reason and the search reports "already aligned" on a frame that is
   visibly off. */

function sadAt(a, b, w, h, dx, dy, step) {
    var sum = 0, n = 0;
    var y0 = Math.max(0, -dy), y1 = Math.min(h, h - dy);
    var x0 = Math.max(0, -dx), x1 = Math.min(w, w - dx);
    for (var y = y0; y < y1; y += step) {
        var ra = y * w, rb = (y + dy) * w;
        for (var x = x0; x < x1; x += step) {
            var v = a[ra + x] - b[rb + x + dx];
            sum += v < 0 ? -v : v;
            n++;
        }
    }
    return n ? sum / n : Infinity;
}

function demean(g) {
    var s = 0;
    for (var i = 0; i < g.length; i++) s += g[i];
    var m = s / g.length;
    var out = new Float32Array(g.length);
    for (var j = 0; j < g.length; j++) out[j] = g[j] - m;
    return out;
}

function searchShift(a, b, w, h, range, step, seedX, seedY) {
    var best = { dx: seedX || 0, dy: seedY || 0, cost: Infinity };
    for (var dy = (seedY || 0) - range; dy <= (seedY || 0) + range; dy++) {
        for (var dx = (seedX || 0) - range; dx <= (seedX || 0) + range; dx++) {
            var c = sadAt(a, b, w, h, dx, dy, step);
            if (c < best.cost) { best = { dx: dx, dy: dy, cost: c }; }
        }
    }
    return best;
}

/**
 * @param baseSrc  frame from the clip that owns the scene (clip 0)
 * @param layerSrc frame from the clip being pasted in
 * @param sceneW   scene width, so the answer comes back in SCENE px
 * @returns { dx, dy, confidence }  dx/dy to ADD to the layer
 */
function registration(baseSrc, layerSrc, sceneW) {
    var CW = 240;
    var aspect = 9 / 16;
    try {
        var sw = layerSrc.videoWidth || layerSrc.width;
        var sh = layerSrc.videoHeight || layerSrc.height;
        if (sw && sh) aspect = sh / sw;
    } catch (_) {}
    var CH = Math.max(8, Math.round(CW * aspect));

    var A = demean(toGray(grab(baseSrc, CW, CH)));
    var B = demean(toGray(grab(layerSrc, CW, CH)));

    var coarse = searchShift(A, B, CW, CH, 12, 2, 0, 0);

    // Refine at 2× so the answer is not quantised to the coarse grid.
    var CW2 = CW * 2, CH2 = CH * 2;
    var A2 = demean(toGray(grab(baseSrc, CW2, CH2)));
    var B2 = demean(toGray(grab(layerSrc, CW2, CH2)));
    var fine = searchShift(A2, B2, CW2, CH2, 3, 2, coarse.dx * 2, coarse.dy * 2);

    // Confidence: how much better the winner is than staying put. A take where
    // the camera really did not move scores ~0 improvement, and reporting that
    // honestly is what lets the UI say "already aligned" instead of nudging by
    // a pixel of noise.
    var zero = sadAt(A2, B2, CW2, CH2, 0, 0, 2);
    var gain = zero > 0 ? (zero - fine.cost) / zero : 0;

    // Two guards against a confident answer about nothing. Both were earned:
    // before them, two BLANK canvases (an element that had not decoded a frame
    // yet) produced a flat cost field, the first candidate scanned won, and the
    // tool "corrected" a 108-px camera shift that never happened.
    //
    //  · a flat field means every shift scores the same — there is no signal;
    //  · a winner sitting ON the edge of the search window means the true
    //    minimum is outside it, so the number returned is a boundary artefact
    //    rather than a measurement.
    var flat = !(zero > 0.5);
    var atEdge = Math.abs(fine.dx - coarse.dx * 2) >= 3 || Math.abs(fine.dy - coarse.dy * 2) >= 3
              || Math.abs(coarse.dx) >= 12 || Math.abs(coarse.dy) >= 12;
    if (flat || atEdge) {
        return { dx: 0, dy: 0, confidence: 0, reason: flat ? 'no-signal' : 'out-of-range' };
    }

    var k = sceneW / CW2;
    return {
        dx: Math.round(-fine.dx * k),
        dy: Math.round(-fine.dy * k),
        confidence: Math.max(0, Math.min(1, gain * 6))
    };
}

/* ───────────────── exposure-invariant difference ─────────────────
   Two takes off one camera on auto-exposure differ by a CONSTANT everywhere,
   and a raw |A−B| turns that constant into a floor under the whole frame.
   Measured on a pair whose second take was 6% brighter: every pixel scored ~14,
   so "these two agree here" was true nowhere, the background could not be
   sampled, and the side-attribution refused to answer at all.

   The offset is removed with a per-channel MEDIAN of the differences, not a
   mean: the performers occupy ~10% of the frame and are exactly the outliers a
   mean would drag the correction toward. */
function channelOffset(da, db) {
    var step = 28;                       // every 7th pixel — plenty for a median
    var r = [], g = [], b = [];
    for (var o = 0; o < da.length; o += step) {
        r.push(da[o] - db[o]);
        g.push(da[o + 1] - db[o + 1]);
        b.push(da[o + 2] - db[o + 2]);
    }
    var m = function (arr) {
        arr.sort(function (x, y) { return x - y; });
        return arr[arr.length >> 1];
    };
    return [m(r), m(g), m(b)];
}

/** Max per-channel deviation after the global offset is taken out. */
function robustDiff(da, db, w, h) {
    var off = channelOffset(da, db);
    var d = new Float32Array(w * h);
    for (var i = 0, o = 0; i < d.length; i++, o += 4) {
        var dr = Math.abs(da[o] - db[o] - off[0]);
        var dg = Math.abs(da[o + 1] - db[o + 1] - off[1]);
        var dbb = Math.abs(da[o + 2] - db[o + 2] - off[2]);
        d[i] = Math.max(dr, Math.max(dg, dbb));
    }
    return d;
}

/* ─────────────────────────── 2. difference map ───────────────────────────
   Returns a canvas whose alpha marks "these two takes disagree here" — i.e.
   the moving subjects. The preview tints it so the user can SEE the corridor
   the seam must stay inside. */

var DIFF_W = 160;

function diffField(baseSrc, layerSrc, w, h) {
    var d = robustDiff(grab(baseSrc, w, h).data, grab(layerSrc, w, h).data, w, h);
    // 3×3 box blur, twice — isolated sensor noise should not read as a person.
    d = boxBlur(d, w, h);
    d = boxBlur(d, w, h);
    return d;
}

function boxBlur(src, w, h) {
    var out = new Float32Array(w * h);
    for (var y = 0; y < h; y++) {
        for (var x = 0; x < w; x++) {
            var s = 0, n = 0;
            for (var j = -1; j <= 1; j++) {
                var yy = y + j; if (yy < 0 || yy >= h) continue;
                for (var i = -1; i <= 1; i++) {
                    var xx = x + i; if (xx < 0 || xx >= w) continue;
                    s += src[yy * w + xx]; n++;
                }
            }
            out[y * w + x] = s / n;
        }
    }
    return out;
}

function differenceMap(baseSrc, layerSrc, threshold) {
    var w = DIFF_W;
    var aspect = 9 / 16;
    try {
        var sw = baseSrc.videoWidth || baseSrc.width, sh = baseSrc.videoHeight || baseSrc.height;
        if (sw && sh) aspect = sh / sw;
    } catch (_) {}
    var h = Math.max(8, Math.round(w * aspect));
    var d = diffField(baseSrc, layerSrc, w, h);

    var thr = threshold == null ? 14 : threshold;
    var cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    var ctx = cv.getContext('2d');
    var img = ctx.createImageData(w, h);
    var px = img.data;
    for (var i = 0, j = 0; i < d.length; i++, j += 4) {
        var a = Math.max(0, Math.min(1, (d[i] - thr) / 40));
        px[j] = 255; px[j + 1] = 64; px[j + 2] = 96;
        px[j + 3] = Math.round(a * 190);
    }
    ctx.putImageData(img, 0, 0);
    return { canvas: cv, field: d, w: w, h: h };
}

/**
 * Best seam for a given angle.
 *
 * "The quietest strip" is NOT the answer on its own, and the difference matters:
 * measured on a real pair, the quietest strip was at 9.5% of the frame — the
 * empty margin outside both performers — and a seam there shows one take almost
 * in full and hides the other completely.
 *
 * The seam has to SEPARATE, so the search is constrained to positions that leave
 * a real share of the disagreement on both sides, and only among those does
 * quietness decide. When no such position exists (everything moving is on one
 * side) the honest answer is the frame's centre, not a confident edge.
 *
 * @returns { pos, separates }
 */
function seamFor(field, w, h, angleDeg) {
    var a = angleDeg * Math.PI / 180;
    var nx = Math.cos(a), ny = Math.sin(a);
    var R = (w * Math.abs(nx) + h * Math.abs(ny)) / 2;
    if (R < 1e-6) return { pos: 0.5, separates: false };

    var BINS = 64;
    var sum = new Float64Array(BINS), cnt = new Float64Array(BINS);
    for (var y = 0; y < h; y++) {
        for (var x = 0; x < w; x++) {
            var dproj = (x - w / 2) * nx + (y - h / 2) * ny;
            var u = (dproj / R + 1) / 2;
            var b = Math.max(0, Math.min(BINS - 1, Math.round(u * (BINS - 1))));
            sum[b] += field[y * w + x];
            cnt[b]++;
        }
    }

    var total = 0;
    for (var t = 0; t < BINS; t++) total += sum[t];
    if (total <= 0) return { pos: 0.5, separates: false };

    // Cumulative energy, so "does this position leave work on both sides?" is
    // one lookup rather than a rescan.
    var cum = new Float64Array(BINS);
    var run = 0;
    for (var c = 0; c < BINS; c++) { run += sum[c]; cum[c] = run; }

    var best = -1, bestVal = Infinity;
    for (var i = 0; i < BINS; i++) {
        var left = cum[i], right = total - cum[i];
        if (left < total * 0.18 || right < total * 0.18) continue;   // must separate

        // Local quietness over a 5-bin window: the seam is feathered, so a
        // single clean bin between two busy ones is not a safe place to cut.
        var sw = 0, sn = 0;
        for (var k = -2; k <= 2; k++) {
            var idx = i + k; if (idx < 0 || idx >= BINS) continue;
            sw += sum[idx]; sn += cnt[idx];
        }
        if (!sn) continue;
        var v = sw / sn;
        if (v < bestVal) { bestVal = v; best = i; }
    }

    if (best < 0) return { pos: 0.5, separates: false, quality: 0 };

    // How CLEAN the cut is, not just where it is. Reference = the average
    // energy of the bins that actually carry a subject; a seam sitting in a
    // genuine gap scores near 1, a seam still slicing through someone scores
    // near 0. Without this the caller cannot tell "found the gap" from "found
    // the least-bad place to cut a person in half", and both look identical
    // from the outside — the tool just quietly ships a broken composite.
    var hotSum = 0, hotCnt = 0;
    for (var b2 = 0; b2 < BINS; b2++) {
        if (!cnt[b2]) continue;
        var dens = sum[b2] / cnt[b2];
        if (dens > 6) { hotSum += sum[b2]; hotCnt += cnt[b2]; }
    }
    var ref = hotCnt ? hotSum / hotCnt : 0;
    var quality = ref > 1e-6 ? Math.max(0, Math.min(1, 1 - bestVal / ref)) : 0;

    return { pos: best / (BINS - 1), separates: true, quality: quality };
}

/** Back-compat shape for the "Find the seam" button, which wants a number. */
function suggestSeam(field, w, h, angleDeg) {
    return seamFor(field, w, h, angleDeg).pos;
}

/* ─────────────── 2b. the opening seam: axis, position AND SIDE ───────────────
   Placing the seam is only half the answer. The other half is which take keeps
   which side, and getting it wrong is not a cosmetic default — it hides BOTH
   performers: the pasted take covers the half where the base take's person
   stands, and shows its own empty half.

   The clean-plate route cannot answer this. A clone performer who stands still
   IS the temporal median of their own take, so the "empty room" contains them
   and the silhouette comes out empty — measured, not assumed.

   What does answer it needs only the two frames, and no background model:

     · |A−B| is non-zero exactly where the two performers are (the room is
       shared, so it cancels). That gives two blobs.
     · The seam goes through the quiet strip between them.
     · For each side, the take that OWNS the person there is the one whose
       colour in the blob is far from the surrounding agreed background. The
       other take is showing plain room in that spot, which by definition looks
       like the room around it.

   Returns null when the frames disagree everywhere or nowhere — a still shot,
   a mismatched pair — so the caller keeps a neutral seam instead of committing
   to a coin flip. */
function analyseSeam(baseSrc, layerSrc) {
    var w = DIFF_W;
    var aspect = 9 / 16;
    try {
        var sw = baseSrc.videoWidth || baseSrc.width, sh = baseSrc.videoHeight || baseSrc.height;
        if (sw && sh) aspect = sh / sw;
    } catch (_) {}
    var h = Math.max(8, Math.round(w * aspect));

    var A = grab(baseSrc, w, h), B = grab(layerSrc, w, h);
    var da = A.data, db = B.data;

    var d = robustDiff(da, db, w, h);
    d = boxBlur(d, w, h);
    d = boxBlur(d, w, h);
    var off = channelOffset(da, db);

    var thr = 22;
    var hot = 0;
    for (var k = 0; k < d.length; k++) if (d[k] > thr) hot++;
    var share = hot / d.length;
    // Nothing moved, or the takes are of different scenes entirely.
    if (share < 0.008 || share > 0.55) return null;

    var best = null;
    [0, 90].forEach(function (angle) {
        var found = seamFor(d, w, h, angle);
        if (!found.separates) return;
        var pos = found.pos;
        var sides = splitEnergy(d, w, h, angle, pos);
        if (sides.lo < hot * 0.12 || sides.hi < hot * 0.12) return;   // both sides must hold a person
        var score = Math.min(sides.lo, sides.hi) / Math.max(1, sides.seam + 1);
        if (!best || score > best.score) {
            best = { angle: angle, pos: pos, score: score, sides: sides, quality: found.quality };
        }
    });
    if (!best) return null;

    // Which take owns the "hi" side (the side the mask would show when inverted)?
    var owner = ownerOfSide(da, db, d, w, h, best.angle, best.pos, thr, true, off);
    if (!owner) return null;

    return {
        angle: best.angle,
        pos: Math.max(0.06, Math.min(0.94, best.pos)),
        // invert === true means the pasted take keeps the far side.
        invert: owner === 'b',
        confidence: Math.max(0, Math.min(1, best.score / 3)),
        // `quality` answers a DIFFERENT question from `confidence`: confidence
        // is "did the side-attribution hold up", quality is "is there actually
        // a gap to cut through". Two performers standing shoulder to shoulder
        // score high confidence and low quality — the tool knows who is where
        // and still cannot separate them with a straight line.
        quality: best.quality
    };
}

/* Energy either side of a seam, and inside a band around it. */
function splitEnergy(d, w, h, angleDeg, pos) {
    var a = angleDeg * Math.PI / 180;
    var nx = Math.cos(a), ny = Math.sin(a);
    var R = (w * Math.abs(nx) + h * Math.abs(ny)) / 2;
    var off = (pos * 2 - 1) * R;
    var band = Math.max(3, R * 0.06);
    var lo = 0, hi = 0, seam = 0;
    for (var y = 0; y < h; y++) {
        for (var x = 0; x < w; x++) {
            var v = d[y * w + x];
            if (v <= 22) continue;
            var p = (x - w / 2) * nx + (y - h / 2) * ny - off;
            if (Math.abs(p) < band) seam++;
            else if (p < 0) lo++;
            else hi++;
        }
    }
    return { lo: lo, hi: hi, seam: seam };
}

/**
 * On one side of the seam, decide whose person it is.
 *
 * The person's colour sits far from the agreed background; plain room does not.
 * Comparing each take's blob colour against the SAME reference makes the test
 * symmetric, so it cannot systematically favour one take.
 */
function ownerOfSide(da, db, d, w, h, angleDeg, pos, thr, farSide, off) {
    off = off || channelOffset(da, db);
    var a = angleDeg * Math.PI / 180;
    var nx = Math.cos(a), ny = Math.sin(a);
    var R = (w * Math.abs(nx) + h * Math.abs(ny)) / 2;
    var off = (pos * 2 - 1) * R;

    var blobA = [0, 0, 0], blobB = [0, 0, 0], nBlob = 0;
    var refA = [0, 0, 0], refB = [0, 0, 0], nRef = 0;

    for (var y = 0; y < h; y++) {
        for (var x = 0; x < w; x++) {
            var p = (x - w / 2) * nx + (y - h / 2) * ny - off;
            if (farSide ? p <= 0 : p >= 0) continue;
            var i = y * w + x, o = i * 4;
            if (d[i] > thr) {
                blobA[0] += da[o]; blobA[1] += da[o + 1]; blobA[2] += da[o + 2];
                // B is compared on A's exposure scale, so the two distances
                // below are answering the same question.
                blobB[0] += db[o] + off[0]; blobB[1] += db[o + 1] + off[1]; blobB[2] += db[o + 2] + off[2];
                nBlob++;
            } else if (d[i] < thr * 0.4) {
                // Agreed pixels on the SAME side — the local room, not the
                // whole-frame average, so a wall and a floor cannot cancel out.
                refA[0] += da[o]; refA[1] += da[o + 1]; refA[2] += da[o + 2];
                refB[0] += db[o] + off[0]; refB[1] += db[o + 1] + off[1]; refB[2] += db[o + 2] + off[2];
                nRef++;
            }
        }
    }
    if (nBlob < 30 || nRef < 60) return null;

    function dist(sum, ref) {
        var dr = sum[0] / nBlob - ref[0] / nRef;
        var dg = sum[1] / nBlob - ref[1] / nRef;
        var dbb = sum[2] / nBlob - ref[2] / nRef;
        return Math.sqrt(dr * dr + dg * dg + dbb * dbb);
    }
    var distA = dist(blobA, refA), distB = dist(blobB, refB);
    // A near-tie is not an answer.
    if (Math.abs(distA - distB) < 6) return null;
    return distA > distB ? 'a' : 'b';
}

/* ─────────────────────────── 3. tone match ───────────────────────────
   Per-channel mean ratio, measured ONLY on pixels the two frames agree about.
   Those pixels are the shared background, which is the only surface that is
   supposed to look identical in both takes. */

function toneMatch(baseSrc, layerSrc) {
    var w = 192;
    var aspect = 9 / 16;
    try {
        var sw = baseSrc.videoWidth || baseSrc.width, sh = baseSrc.videoHeight || baseSrc.height;
        if (sw && sh) aspect = sh / sw;
    } catch (_) {}
    var h = Math.max(8, Math.round(w * aspect));

    var A = grab(baseSrc, w, h), B = grab(layerSrc, w, h);
    var da = A.data, db = B.data;

    // Per channel, fit  A ≈ gain·B + offset  by least squares over the pixels
    // the two takes AGREE about, i.e. the shared room.
    //
    // Gain alone is not enough, and the difference is visible rather than
    // theoretical: a camera that lifts the black level between takes shifts the
    // picture ADDITIVELY, and a pure multiplier leaves a bright step running
    // down the seam — the exact defect this tool exists to avoid. Two unknowns
    // per channel cost nothing here and cover both cases.
    var n = 0;
    var sa = [0, 0, 0], sb = [0, 0, 0], sbb = [0, 0, 0], sab = [0, 0, 0];
    for (var i = 0; i < da.length; i += 4) {
        var lumA = 0.299 * da[i] + 0.587 * da[i + 1] + 0.114 * da[i + 2];
        var lumB = 0.299 * db[i] + 0.587 * db[i + 1] + 0.114 * db[i + 2];
        if (Math.abs(lumA - lumB) > 26) continue;      // a subject stands here
        if (lumA < 6 || lumA > 249) continue;           // clipped: carries no ratio
        for (var c = 0; c < 3; c++) {
            var av = da[i + c], bv = db[i + c];
            sa[c] += av; sb[c] += bv; sbb[c] += bv * bv; sab[c] += av * bv;
        }
        n++;
    }
    // Too little agreeing background to measure — say so instead of inventing a
    // correction out of forty pixels.
    if (n < (w * h) * 0.08) return null;

    var gain = [1, 1, 1], offset = [0, 0, 0];
    for (var k = 0; k < 3; k++) {
        var mA = sa[k] / n, mB = sb[k] / n;
        var varB = sbb[k] / n - mB * mB;
        var covAB = sab[k] / n - mA * mB;
        // A flat channel (a monochrome wall) has no variance to fit a slope to;
        // there the honest model is a pure offset.
        var g = varB > 4 ? covAB / varB : 1;
        g = Math.max(0.5, Math.min(2.0, g));
        var o = mA - g * mB;
        o = Math.max(-80, Math.min(80, o));
        gain[k] = g; offset[k] = o;
    }

    var exposure = (gain[0] + gain[1] + gain[2]) / 3;
    return {
        exposure: exposure,
        tint: [gain[0] / exposure, gain[1] / exposure, gain[2] / exposure],
        offset: offset,
        coverage: n / (w * h)
    };
}

window.CloneAlign = {
    registration: registration,
    differenceMap: differenceMap,
    suggestSeam: suggestSeam,
    seamFor: seamFor,
    // Internals exposed for the regression tests: every guard in analyseSeam
    // can refuse, and a test that only sees `null` cannot say WHICH one did.
    _splitEnergy: splitEnergy,
    _ownerOfSide: ownerOfSide,
    _diffField: diffField,
    _robustDiff: robustDiff,
    _channelOffset: channelOffset,
    analyseSeam: analyseSeam,
    toneMatch: toneMatch
};
})();
